/**
 * Bridges the module-level axios interceptor to the React step-up modal.
 *
 * The interceptor (handleApiResponseError) can't use React context, so the
 * StepUpProvider registers a handler here on mount, mirroring the pattern used by
 * `authHeaderProviderRef` in the mobile app. When a home-health-api response is
 * `403 STEP_UP_REQUIRED`, the interceptor calls `requestStepUp(...)`, which drives the
 * modal and resolves true (re-authenticated → retry) or false (cancelled → soft-block).
 */
export type StepUpMethod = "sso" | "magic_link" | "otp";

export interface StepUpRequest {
  stepUpMethod: StepUpMethod;
}

type StepUpHandler = (request: StepUpRequest) => Promise<boolean>;

// How long a torn-down provider gets to re-register (remount/fast-refresh) before an
// in-flight prompt is released as a soft-block. A remount's cleanup→setup runs within
// one commit, so this only needs to outlast a couple of frames — a genuine teardown
// must not leave blocked requests hanging forever.
const REREGISTER_GRACE_MS = 250;

let handler: StepUpHandler | undefined;
// Coalesce concurrent 403s (e.g. a page firing several PHI queries) into one prompt.
let inFlight: Promise<boolean> | undefined;
// Resolver + originating request for the coalesced prompt, kept so a provider remount can
// re-drive the NEW host with the same request instead of abandoning the blocked retries.
let inFlightResolve: ((reAuthenticated: boolean) => void) | undefined;
let pendingRequest: StepUpRequest | undefined;
let releaseTimer: ReturnType<typeof setTimeout> | undefined;

export function registerStepUpHandler(next: StepUpHandler | undefined): void {
  handler = next;
  if (releaseTimer) {
    clearTimeout(releaseTimer);
    releaseTimer = undefined;
  }

  if (!next) {
    // Provider teardown with a prompt in flight: don't resolve false immediately — a
    // remount (fast-refresh, provider re-mount) re-registers within the same commit and
    // must inherit the prompt. Only if no host returns do we release as a soft-block.
    if (inFlightResolve) {
      const resolve = inFlightResolve;
      releaseTimer = setTimeout(() => {
        releaseTimer = undefined;
        resolve(false);
      }, REREGISTER_GRACE_MS);
    }

    return;
  }

  // A new provider registered while a prompt is in flight (remount superseded the old
  // modal). Re-drive it with the stored request; the blocked requests stay parked on the
  // same coalesced promise, so verifying on the new modal still retries all of them. The
  // superseded provider's own handler promise can no longer settle (its resolver
  // unmounted), so wiring the new outcome to the same resolver is safe — first wins.
  if (pendingRequest && inFlightResolve) {
    const resolve = inFlightResolve;
    try {
      next(pendingRequest).then(resolve, () => {
        resolve(false);
      });
    } catch {
      resolve(false);
    }
  }
}

export async function requestStepUp(request: StepUpRequest): Promise<boolean> {
  // Join an in-flight prompt even while the handler is momentarily unregistered (the
  // re-register grace window) — the prompt can still succeed once a new provider mounts.
  if (inFlight) {
    return inFlight;
  }

  if (!handler) {
    return false;
  }

  const activeHandler = handler;
  pendingRequest = request;
  inFlight = new Promise<boolean>((resolve) => {
    inFlightResolve = resolve;
    // The axios interceptor relies on this promise never rejecting: a handler that
    // throws synchronously must soft-block, not replace the original API error.
    try {
      activeHandler(request).then(resolve, () => {
        resolve(false);
      });
    } catch {
      resolve(false);
    }
  }).finally(() => {
    inFlight = undefined;
    inFlightResolve = undefined;
    pendingRequest = undefined;
    if (releaseTimer) {
      clearTimeout(releaseTimer);
      releaseTimer = undefined;
    }
  });

  return inFlight;
}
